<template>
  <div>
    <h2 class="question-title with-subtext">
      Let’s get started!
    </h2>
    <p
      v-if="isOnboardingRedesign"
      class="sub-text group-mb"
    >
      Please share your email and create a password so we can save your intake information.
    </p>
    <p
      v-else
      class="sub-text group-mb"
    >
      Our doctors will help prescribe what’s right for you. Share your email so
      we can save your answers. We won’t fill up your inbox!
    </p>
    <p v-if="!isOnboardingRedesign">
      Please provide your email address.
    </p>
    <b-row>
      <b-col cols="12">
        <div
          role="group"
          class="group-mb"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="email"
            class="input-label"
          >Email Address*</label>
          <b-form-input
            id="email"
            v-model="email"
            class="fs-mask onboarding-input"
            type="email"
            autocomplete="off"
            :state="emailState"
            :placeholder="isOnboardingRedesign ? 'Email Address *' : ''"
            required
          />
          <b-form-invalid-feedback
            v-if="!isOnboardingRedesign"
            id="input-live-feedback"
          >
            The email must have '@email.com'
          </b-form-invalid-feedback>
        </div>
      </b-col>

      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? '12' : '6'"
      >
        <div
          role="group"
          class="group-mb tw-relative"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="password"
            class="input-label"
          >Password*</label>
          <b-form-input
            id="password"
            v-model="password"
            class="fs-mask onboarding-input"
            :type="isOnboardingRedesign && isPasswordShown ? 'text' : 'password'"
            :state="passwordState"
            autocomplete="off"
            :placeholder="isOnboardingRedesign ? 'Password *' : ''"
            :formatter="limitCharacters"
            required
          />
          <!-- This will only be shown if the preceding input has an invalid state -->
          <b-form-invalid-feedback
            v-if="!isOnboardingRedesign"
            id="input-live-feedback"
          >
            Password must be a minimum of 8 characters and a maximum of 50.
          </b-form-invalid-feedback>
          <div
            v-if="isOnboardingRedesign"
            class="tw-cursor-pointer tw-absolute tw-top-0 tw-right-0 tw-px-2 tw-pt-4 tw-pb-2 tw-flex tw-items-center tw-bg-white"
            @click="isPasswordShown = !isPasswordShown"
          >
            <svg
              v-if="isPasswordShown"
              width="17"
              height="12"
              viewBox="0 0 17 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.20508 1.05469C8.99023 1.05469 9.73242 1.15039 10.4316 1.3418C11.1348 1.5293 11.7832 1.7793 12.377 2.0918C12.9746 2.40039 13.5078 2.74219 13.9766 3.11719C14.4492 3.49219 14.8516 3.86914 15.1836 4.24805C15.5156 4.62305 15.7695 4.97266 15.9453 5.29688C16.1211 5.61719 16.209 5.87695 16.209 6.07617C16.209 6.29883 16.0996 6.59766 15.8809 6.97266C15.6621 7.34766 15.3457 7.75195 14.9316 8.18555C14.5215 8.61523 14.0293 9.02539 13.4551 9.41602L12.7461 8.71289C13.1172 8.4668 13.4531 8.21289 13.7539 7.95117C14.0547 7.68945 14.3105 7.4375 14.5215 7.19531C14.7363 6.95312 14.9004 6.73438 15.0137 6.53906C15.127 6.34375 15.1836 6.18945 15.1836 6.07617C15.1836 5.95898 15.0996 5.7793 14.9316 5.53711C14.7676 5.29492 14.5332 5.01953 14.2285 4.71094C13.9238 4.39844 13.5586 4.08398 13.1328 3.76758C12.7109 3.45117 12.2402 3.1582 11.7207 2.88867C11.2012 2.61523 10.6445 2.39648 10.0508 2.23242C9.46094 2.06445 8.8457 1.98047 8.20508 1.98047C7.86914 1.98047 7.54102 2.00781 7.2207 2.0625C6.9043 2.11328 6.59961 2.18359 6.30664 2.27344L5.52734 1.49414C5.9375 1.36133 6.36523 1.25586 6.81055 1.17773C7.25977 1.0957 7.72461 1.05469 8.20508 1.05469ZM8.20508 11.0977C7.42383 11.0977 6.68359 11.002 5.98438 10.8105C5.28906 10.623 4.64258 10.375 4.04492 10.0664C3.45117 9.75391 2.91797 9.41016 2.44531 9.03516C1.97266 8.66016 1.56836 8.28516 1.23242 7.91016C0.896484 7.53125 0.638672 7.18164 0.458984 6.86133C0.283203 6.53711 0.195312 6.27539 0.195312 6.07617C0.195312 5.9043 0.261719 5.6875 0.394531 5.42578C0.527344 5.16016 0.720703 4.86914 0.974609 4.55273C1.22852 4.23633 1.53516 3.91406 1.89453 3.58594C2.25781 3.25391 2.66797 2.9375 3.125 2.63672L3.85156 3.36328C3.44922 3.62109 3.08594 3.88672 2.76172 4.16016C2.44141 4.43359 2.16602 4.69922 1.93555 4.95703C1.70508 5.21094 1.52734 5.4375 1.40234 5.63672C1.28125 5.83203 1.2207 5.97852 1.2207 6.07617C1.2207 6.21289 1.30273 6.4082 1.4668 6.66211C1.63086 6.91602 1.86523 7.19727 2.16992 7.50586C2.47852 7.81055 2.84375 8.11914 3.26562 8.43164C3.6875 8.74414 4.1582 9.0332 4.67773 9.29883C5.19727 9.56055 5.75391 9.77148 6.34766 9.93164C6.94141 10.0918 7.56055 10.1719 8.20508 10.1719C8.57617 10.1719 8.93945 10.1426 9.29492 10.084C9.6543 10.0254 10 9.94336 10.332 9.83789L11.0938 10.6055C10.6562 10.7578 10.1953 10.877 9.71094 10.9629C9.22656 11.0527 8.72461 11.0977 8.20508 11.0977ZM8.20508 2.79492C8.6582 2.79492 9.08398 2.87891 9.48242 3.04688C9.88086 3.21484 10.2305 3.44922 10.5312 3.75C10.832 4.04688 11.0664 4.39453 11.2344 4.79297C11.4062 5.19141 11.4922 5.61914 11.4922 6.07617C11.4922 6.2793 11.4707 6.47656 11.4277 6.66797C11.3887 6.85938 11.332 7.04492 11.2578 7.22461L7.05664 3.02344C7.23242 2.95312 7.41602 2.89844 7.60742 2.85938C7.80273 2.81641 8.00195 2.79492 8.20508 2.79492ZM8.20508 9.35742C7.74414 9.35742 7.31445 9.27148 6.91602 9.09961C6.51758 8.92773 6.16797 8.69141 5.86719 8.39062C5.56641 8.08594 5.33008 7.73633 5.1582 7.3418C4.99023 6.94336 4.90625 6.52148 4.90625 6.07617C4.90625 5.83398 4.93164 5.59961 4.98242 5.37305C5.03711 5.14258 5.11328 4.92578 5.21094 4.72266L9.54688 9.05273C9.34375 9.15039 9.12891 9.22656 8.90234 9.28125C8.67578 9.33203 8.44336 9.35742 8.20508 9.35742ZM12.7812 11.1504L2.97852 1.35938C2.89258 1.27344 2.84961 1.16797 2.84961 1.04297C2.84961 0.914062 2.89258 0.806641 2.97852 0.720703C3.06836 0.630859 3.17578 0.587891 3.30078 0.591797C3.42969 0.591797 3.53711 0.634766 3.62305 0.720703L13.4199 10.5117C13.5098 10.6016 13.5566 10.7051 13.5605 10.8223C13.5645 10.9434 13.5176 11.0527 13.4199 11.1504C13.3301 11.248 13.2227 11.2949 13.0977 11.291C12.9766 11.2871 12.8711 11.2402 12.7812 11.1504Z"
                fill="#1A1A1A"
              />
            </svg>
            <svg
              v-else
              width="17"
              height="11"
              viewBox="0 0 17 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.20508 10.0977C7.42383 10.0977 6.68359 10.002 5.98438 9.81055C5.28906 9.62305 4.64258 9.375 4.04492 9.06641C3.45117 8.75391 2.91797 8.41016 2.44531 8.03516C1.97266 7.66016 1.56836 7.28516 1.23242 6.91016C0.896484 6.53125 0.638672 6.18164 0.458984 5.86133C0.283203 5.53711 0.195312 5.27539 0.195312 5.07617C0.195312 4.87695 0.283203 4.61719 0.458984 4.29688C0.638672 3.97266 0.896484 3.62305 1.23242 3.24805C1.56836 2.86914 1.97266 2.49219 2.44531 2.11719C2.92188 1.74219 3.45703 1.40039 4.05078 1.0918C4.64844 0.779297 5.29492 0.529297 5.99023 0.341797C6.68945 0.150391 7.42773 0.0546875 8.20508 0.0546875C8.99023 0.0546875 9.73242 0.150391 10.4316 0.341797C11.1348 0.529297 11.7832 0.779297 12.377 1.0918C12.9746 1.40039 13.5078 1.74219 13.9766 2.11719C14.4492 2.49219 14.8516 2.86914 15.1836 3.24805C15.5156 3.62305 15.7695 3.97266 15.9453 4.29688C16.1211 4.61719 16.209 4.87695 16.209 5.07617C16.209 5.27539 16.1211 5.53711 15.9453 5.86133C15.7695 6.18164 15.5156 6.53125 15.1836 6.91016C14.8516 7.28516 14.4492 7.66016 13.9766 8.03516C13.5078 8.41016 12.9766 8.75391 12.3828 9.06641C11.7891 9.375 11.1406 9.62305 10.4375 9.81055C9.73828 10.002 8.99414 10.0977 8.20508 10.0977ZM8.20508 9.17188C8.8457 9.17188 9.46094 9.0918 10.0508 8.93164C10.6445 8.77148 11.2012 8.56055 11.7207 8.29883C12.2402 8.0332 12.7109 7.74414 13.1328 7.43164C13.5586 7.11914 13.9238 6.81055 14.2285 6.50586C14.5332 6.19727 14.7676 5.91602 14.9316 5.66211C15.0996 5.4082 15.1836 5.21289 15.1836 5.07617C15.1836 4.95898 15.0996 4.7793 14.9316 4.53711C14.7676 4.29492 14.5332 4.01953 14.2285 3.71094C13.9238 3.39844 13.5586 3.08398 13.1328 2.76758C12.7109 2.45117 12.2402 2.1582 11.7207 1.88867C11.2012 1.61523 10.6445 1.39648 10.0508 1.23242C9.46094 1.06445 8.8457 0.980469 8.20508 0.980469C7.56055 0.980469 6.94141 1.06445 6.34766 1.23242C5.75391 1.39648 5.19727 1.61523 4.67773 1.88867C4.1582 2.1582 3.6875 2.45117 3.26562 2.76758C2.84375 3.08398 2.47852 3.39844 2.16992 3.71094C1.86523 4.01953 1.63086 4.29492 1.4668 4.53711C1.30273 4.7793 1.2207 4.95898 1.2207 5.07617C1.2207 5.21289 1.30273 5.4082 1.4668 5.66211C1.63086 5.91602 1.86523 6.19727 2.16992 6.50586C2.47852 6.81055 2.84375 7.11914 3.26562 7.43164C3.6875 7.74414 4.1582 8.0332 4.67773 8.29883C5.19727 8.56055 5.75391 8.77148 6.34766 8.93164C6.94141 9.0918 7.56055 9.17188 8.20508 9.17188ZM8.20508 8.35742C7.74414 8.35742 7.31445 8.27148 6.91602 8.09961C6.51758 7.92773 6.16797 7.69141 5.86719 7.39062C5.56641 7.08594 5.33008 6.73633 5.1582 6.3418C4.99023 5.94336 4.90625 5.52148 4.90625 5.07617C4.90625 4.61914 4.99023 4.19141 5.1582 3.79297C5.33008 3.39453 5.56641 3.04688 5.86719 2.75C6.16797 2.44922 6.51758 2.21484 6.91602 2.04688C7.31445 1.87891 7.74414 1.79492 8.20508 1.79492C8.6582 1.79492 9.08398 1.87891 9.48242 2.04688C9.88086 2.21484 10.2305 2.44922 10.5312 2.75C10.832 3.04688 11.0664 3.39453 11.2344 3.79297C11.4062 4.19141 11.4922 4.61914 11.4922 5.07617C11.4922 5.52148 11.4062 5.94336 11.2344 6.3418C11.0664 6.73633 10.832 7.08594 10.5312 7.39062C10.2305 7.69141 9.88086 7.92773 9.48242 8.09961C9.08398 8.27148 8.6582 8.35742 8.20508 8.35742ZM8.20508 6.16602C8.50586 6.16602 8.76367 6.05859 8.97852 5.84375C9.19336 5.62891 9.30078 5.37305 9.30078 5.07617C9.30078 4.77539 9.19336 4.51953 8.97852 4.30859C8.76367 4.09375 8.50586 3.98633 8.20508 3.98633C7.90039 3.98633 7.64062 4.09375 7.42578 4.30859C7.21094 4.51953 7.10352 4.77539 7.10352 5.07617C7.10352 5.37305 7.21094 5.62891 7.42578 5.84375C7.64062 6.05859 7.90039 6.16602 8.20508 6.16602Z"
                :fill="password ? '#1A1A1A' : '#C7C7C7'"
              />
            </svg>
          </div>
        </div>
      </b-col>

      <b-col
        cols="12"
        :lg="isOnboardingRedesign ? '12' : '6'"
      >
        <div
          role="group"
          class="group-mb tw-relative"
        >
          <label
            v-if="!isOnboardingRedesign"
            for="cpassword"
            class="input-label"
          >Confirm Password*</label>
          <b-form-input
            id="cpassword"
            v-model="cpassword"
            class="fs-mask onboarding-input"
            :type="isOnboardingRedesign && isCPasswordShown ? 'text' : 'password'"
            :state="cpasswordState"
            autocomplete="off"
            required
            :placeholder="isOnboardingRedesign ? 'Confirm Password *' : ''"
            :formatter="limitCharacters"
            @keyup.enter="register"
          />
          <!-- This will only be shown if the preceding input has an invalid state -->
          <b-form-invalid-feedback
            v-if="!isOnboardingRedesign"
            id="input-live-feedback"
          >
            Password do not match
          </b-form-invalid-feedback>
          <div
            v-if="isOnboardingRedesign"
            class="tw-cursor-pointer tw-absolute tw-top-0 tw-right-0 tw-px-2 tw-pt-4 tw-pb-2 tw-flex tw-items-center tw-bg-white"
            @click="isCPasswordShown = !isCPasswordShown"
          >
            <svg
              v-if="isCPasswordShown"
              width="17"
              height="12"
              viewBox="0 0 17 12"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.20508 1.05469C8.99023 1.05469 9.73242 1.15039 10.4316 1.3418C11.1348 1.5293 11.7832 1.7793 12.377 2.0918C12.9746 2.40039 13.5078 2.74219 13.9766 3.11719C14.4492 3.49219 14.8516 3.86914 15.1836 4.24805C15.5156 4.62305 15.7695 4.97266 15.9453 5.29688C16.1211 5.61719 16.209 5.87695 16.209 6.07617C16.209 6.29883 16.0996 6.59766 15.8809 6.97266C15.6621 7.34766 15.3457 7.75195 14.9316 8.18555C14.5215 8.61523 14.0293 9.02539 13.4551 9.41602L12.7461 8.71289C13.1172 8.4668 13.4531 8.21289 13.7539 7.95117C14.0547 7.68945 14.3105 7.4375 14.5215 7.19531C14.7363 6.95312 14.9004 6.73438 15.0137 6.53906C15.127 6.34375 15.1836 6.18945 15.1836 6.07617C15.1836 5.95898 15.0996 5.7793 14.9316 5.53711C14.7676 5.29492 14.5332 5.01953 14.2285 4.71094C13.9238 4.39844 13.5586 4.08398 13.1328 3.76758C12.7109 3.45117 12.2402 3.1582 11.7207 2.88867C11.2012 2.61523 10.6445 2.39648 10.0508 2.23242C9.46094 2.06445 8.8457 1.98047 8.20508 1.98047C7.86914 1.98047 7.54102 2.00781 7.2207 2.0625C6.9043 2.11328 6.59961 2.18359 6.30664 2.27344L5.52734 1.49414C5.9375 1.36133 6.36523 1.25586 6.81055 1.17773C7.25977 1.0957 7.72461 1.05469 8.20508 1.05469ZM8.20508 11.0977C7.42383 11.0977 6.68359 11.002 5.98438 10.8105C5.28906 10.623 4.64258 10.375 4.04492 10.0664C3.45117 9.75391 2.91797 9.41016 2.44531 9.03516C1.97266 8.66016 1.56836 8.28516 1.23242 7.91016C0.896484 7.53125 0.638672 7.18164 0.458984 6.86133C0.283203 6.53711 0.195312 6.27539 0.195312 6.07617C0.195312 5.9043 0.261719 5.6875 0.394531 5.42578C0.527344 5.16016 0.720703 4.86914 0.974609 4.55273C1.22852 4.23633 1.53516 3.91406 1.89453 3.58594C2.25781 3.25391 2.66797 2.9375 3.125 2.63672L3.85156 3.36328C3.44922 3.62109 3.08594 3.88672 2.76172 4.16016C2.44141 4.43359 2.16602 4.69922 1.93555 4.95703C1.70508 5.21094 1.52734 5.4375 1.40234 5.63672C1.28125 5.83203 1.2207 5.97852 1.2207 6.07617C1.2207 6.21289 1.30273 6.4082 1.4668 6.66211C1.63086 6.91602 1.86523 7.19727 2.16992 7.50586C2.47852 7.81055 2.84375 8.11914 3.26562 8.43164C3.6875 8.74414 4.1582 9.0332 4.67773 9.29883C5.19727 9.56055 5.75391 9.77148 6.34766 9.93164C6.94141 10.0918 7.56055 10.1719 8.20508 10.1719C8.57617 10.1719 8.93945 10.1426 9.29492 10.084C9.6543 10.0254 10 9.94336 10.332 9.83789L11.0938 10.6055C10.6562 10.7578 10.1953 10.877 9.71094 10.9629C9.22656 11.0527 8.72461 11.0977 8.20508 11.0977ZM8.20508 2.79492C8.6582 2.79492 9.08398 2.87891 9.48242 3.04688C9.88086 3.21484 10.2305 3.44922 10.5312 3.75C10.832 4.04688 11.0664 4.39453 11.2344 4.79297C11.4062 5.19141 11.4922 5.61914 11.4922 6.07617C11.4922 6.2793 11.4707 6.47656 11.4277 6.66797C11.3887 6.85938 11.332 7.04492 11.2578 7.22461L7.05664 3.02344C7.23242 2.95312 7.41602 2.89844 7.60742 2.85938C7.80273 2.81641 8.00195 2.79492 8.20508 2.79492ZM8.20508 9.35742C7.74414 9.35742 7.31445 9.27148 6.91602 9.09961C6.51758 8.92773 6.16797 8.69141 5.86719 8.39062C5.56641 8.08594 5.33008 7.73633 5.1582 7.3418C4.99023 6.94336 4.90625 6.52148 4.90625 6.07617C4.90625 5.83398 4.93164 5.59961 4.98242 5.37305C5.03711 5.14258 5.11328 4.92578 5.21094 4.72266L9.54688 9.05273C9.34375 9.15039 9.12891 9.22656 8.90234 9.28125C8.67578 9.33203 8.44336 9.35742 8.20508 9.35742ZM12.7812 11.1504L2.97852 1.35938C2.89258 1.27344 2.84961 1.16797 2.84961 1.04297C2.84961 0.914062 2.89258 0.806641 2.97852 0.720703C3.06836 0.630859 3.17578 0.587891 3.30078 0.591797C3.42969 0.591797 3.53711 0.634766 3.62305 0.720703L13.4199 10.5117C13.5098 10.6016 13.5566 10.7051 13.5605 10.8223C13.5645 10.9434 13.5176 11.0527 13.4199 11.1504C13.3301 11.248 13.2227 11.2949 13.0977 11.291C12.9766 11.2871 12.8711 11.2402 12.7812 11.1504Z"
                fill="#1A1A1A"
              />
            </svg>
            <svg
              v-else
              width="17"
              height="11"
              viewBox="0 0 17 11"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M8.20508 10.0977C7.42383 10.0977 6.68359 10.002 5.98438 9.81055C5.28906 9.62305 4.64258 9.375 4.04492 9.06641C3.45117 8.75391 2.91797 8.41016 2.44531 8.03516C1.97266 7.66016 1.56836 7.28516 1.23242 6.91016C0.896484 6.53125 0.638672 6.18164 0.458984 5.86133C0.283203 5.53711 0.195312 5.27539 0.195312 5.07617C0.195312 4.87695 0.283203 4.61719 0.458984 4.29688C0.638672 3.97266 0.896484 3.62305 1.23242 3.24805C1.56836 2.86914 1.97266 2.49219 2.44531 2.11719C2.92188 1.74219 3.45703 1.40039 4.05078 1.0918C4.64844 0.779297 5.29492 0.529297 5.99023 0.341797C6.68945 0.150391 7.42773 0.0546875 8.20508 0.0546875C8.99023 0.0546875 9.73242 0.150391 10.4316 0.341797C11.1348 0.529297 11.7832 0.779297 12.377 1.0918C12.9746 1.40039 13.5078 1.74219 13.9766 2.11719C14.4492 2.49219 14.8516 2.86914 15.1836 3.24805C15.5156 3.62305 15.7695 3.97266 15.9453 4.29688C16.1211 4.61719 16.209 4.87695 16.209 5.07617C16.209 5.27539 16.1211 5.53711 15.9453 5.86133C15.7695 6.18164 15.5156 6.53125 15.1836 6.91016C14.8516 7.28516 14.4492 7.66016 13.9766 8.03516C13.5078 8.41016 12.9766 8.75391 12.3828 9.06641C11.7891 9.375 11.1406 9.62305 10.4375 9.81055C9.73828 10.002 8.99414 10.0977 8.20508 10.0977ZM8.20508 9.17188C8.8457 9.17188 9.46094 9.0918 10.0508 8.93164C10.6445 8.77148 11.2012 8.56055 11.7207 8.29883C12.2402 8.0332 12.7109 7.74414 13.1328 7.43164C13.5586 7.11914 13.9238 6.81055 14.2285 6.50586C14.5332 6.19727 14.7676 5.91602 14.9316 5.66211C15.0996 5.4082 15.1836 5.21289 15.1836 5.07617C15.1836 4.95898 15.0996 4.7793 14.9316 4.53711C14.7676 4.29492 14.5332 4.01953 14.2285 3.71094C13.9238 3.39844 13.5586 3.08398 13.1328 2.76758C12.7109 2.45117 12.2402 2.1582 11.7207 1.88867C11.2012 1.61523 10.6445 1.39648 10.0508 1.23242C9.46094 1.06445 8.8457 0.980469 8.20508 0.980469C7.56055 0.980469 6.94141 1.06445 6.34766 1.23242C5.75391 1.39648 5.19727 1.61523 4.67773 1.88867C4.1582 2.1582 3.6875 2.45117 3.26562 2.76758C2.84375 3.08398 2.47852 3.39844 2.16992 3.71094C1.86523 4.01953 1.63086 4.29492 1.4668 4.53711C1.30273 4.7793 1.2207 4.95898 1.2207 5.07617C1.2207 5.21289 1.30273 5.4082 1.4668 5.66211C1.63086 5.91602 1.86523 6.19727 2.16992 6.50586C2.47852 6.81055 2.84375 7.11914 3.26562 7.43164C3.6875 7.74414 4.1582 8.0332 4.67773 8.29883C5.19727 8.56055 5.75391 8.77148 6.34766 8.93164C6.94141 9.0918 7.56055 9.17188 8.20508 9.17188ZM8.20508 8.35742C7.74414 8.35742 7.31445 8.27148 6.91602 8.09961C6.51758 7.92773 6.16797 7.69141 5.86719 7.39062C5.56641 7.08594 5.33008 6.73633 5.1582 6.3418C4.99023 5.94336 4.90625 5.52148 4.90625 5.07617C4.90625 4.61914 4.99023 4.19141 5.1582 3.79297C5.33008 3.39453 5.56641 3.04688 5.86719 2.75C6.16797 2.44922 6.51758 2.21484 6.91602 2.04688C7.31445 1.87891 7.74414 1.79492 8.20508 1.79492C8.6582 1.79492 9.08398 1.87891 9.48242 2.04688C9.88086 2.21484 10.2305 2.44922 10.5312 2.75C10.832 3.04688 11.0664 3.39453 11.2344 3.79297C11.4062 4.19141 11.4922 4.61914 11.4922 5.07617C11.4922 5.52148 11.4062 5.94336 11.2344 6.3418C11.0664 6.73633 10.832 7.08594 10.5312 7.39062C10.2305 7.69141 9.88086 7.92773 9.48242 8.09961C9.08398 8.27148 8.6582 8.35742 8.20508 8.35742ZM8.20508 6.16602C8.50586 6.16602 8.76367 6.05859 8.97852 5.84375C9.19336 5.62891 9.30078 5.37305 9.30078 5.07617C9.30078 4.77539 9.19336 4.51953 8.97852 4.30859C8.76367 4.09375 8.50586 3.98633 8.20508 3.98633C7.90039 3.98633 7.64062 4.09375 7.42578 4.30859C7.21094 4.51953 7.10352 4.77539 7.10352 5.07617C7.10352 5.37305 7.21094 5.62891 7.42578 5.84375C7.64062 6.05859 7.90039 6.16602 8.20508 6.16602Z"
                :fill="cpassword ? '#1A1A1A' : '#C7C7C7'"
              />
            </svg>
          </div>
        </div>
      </b-col>
    </b-row>

    <div
      v-if="isOnboardingRedesign"
      class="tw-pt-0 tw-pb-4 tw-flex tw-items-center tw-gap-2"
    >
      <svg
        width="14"
        height="15"
        viewBox="0 0 14 15"
        fill="none"
        xmlns="http://www.w3.org/2000/svg"
      >
        <path
          d="M6.97266 14.2422C6.02018 14.2422 5.1224 14.0599 4.2793 13.6953C3.44076 13.3307 2.7002 12.8271 2.05762 12.1846C1.41504 11.542 0.911458 10.8014 0.546875 9.96289C0.182292 9.11979 0 8.22201 0 7.26953C0 6.31706 0.182292 5.42155 0.546875 4.58301C0.911458 3.73991 1.41276 2.99707 2.05078 2.35449C2.69336 1.71191 3.43392 1.20833 4.27246 0.84375C5.11556 0.479167 6.01335 0.296875 6.96582 0.296875C7.91829 0.296875 8.81608 0.479167 9.65918 0.84375C10.5023 1.20833 11.2451 1.71191 11.8877 2.35449C12.5303 2.99707 13.0339 3.73991 13.3984 4.58301C13.763 5.42155 13.9453 6.31706 13.9453 7.26953C13.9453 8.22201 13.763 9.11979 13.3984 9.96289C13.0339 10.8014 12.5303 11.542 11.8877 12.1846C11.2451 12.8271 10.5023 13.3307 9.65918 13.6953C8.82064 14.0599 7.92513 14.2422 6.97266 14.2422ZM6.97266 13.0801C7.7793 13.0801 8.53353 12.9297 9.23535 12.6289C9.93717 12.3281 10.5547 11.9134 11.0879 11.3848C11.6211 10.8516 12.0358 10.234 12.332 9.53223C12.6328 8.8304 12.7832 8.07617 12.7832 7.26953C12.7832 6.46289 12.6328 5.70866 12.332 5.00684C12.0312 4.30501 11.6143 3.6875 11.0811 3.1543C10.5524 2.62109 9.9349 2.20638 9.22852 1.91016C8.52669 1.60938 7.77246 1.45898 6.96582 1.45898C6.15918 1.45898 5.40495 1.60938 4.70312 1.91016C4.0013 2.20638 3.38607 2.62109 2.85742 3.1543C2.32878 3.6875 1.91406 4.30501 1.61328 5.00684C1.31706 5.70866 1.16895 6.46289 1.16895 7.26953C1.16895 8.07617 1.31706 8.8304 1.61328 9.53223C1.91406 10.234 2.32878 10.8516 2.85742 11.3848C3.39062 11.9134 4.00814 12.3281 4.70996 12.6289C5.41178 12.9297 6.16602 13.0801 6.97266 13.0801ZM6.96582 8.5C6.61491 8.5 6.4349 8.31999 6.42578 7.95996L6.33691 4.34375C6.33236 4.17057 6.38704 4.02702 6.50098 3.91309C6.61947 3.79915 6.77214 3.74219 6.95898 3.74219C7.14128 3.74219 7.29167 3.80143 7.41016 3.91992C7.5332 4.03385 7.59245 4.17741 7.58789 4.35059L7.48535 7.95996C7.48079 8.31999 7.30762 8.5 6.96582 8.5ZM6.96582 10.7217C6.7653 10.7217 6.59212 10.6533 6.44629 10.5166C6.30046 10.3753 6.22754 10.2067 6.22754 10.0107C6.22754 9.81478 6.30046 9.64844 6.44629 9.51172C6.59212 9.37044 6.7653 9.2998 6.96582 9.2998C7.16634 9.2998 7.33952 9.36816 7.48535 9.50488C7.63119 9.6416 7.7041 9.81022 7.7041 10.0107C7.7041 10.2113 7.62891 10.3799 7.47852 10.5166C7.33268 10.6533 7.16178 10.7217 6.96582 10.7217Z"
          fill="#1A6A72"
        />
      </svg>
      <div>
        Fields marked with<span class="tw-text-[#1A6A72] tw-px-0.5">*</span>are required.
      </div>
    </div>
    <ErrorLabel v-if="isOnboardingRedesign && formError">
      {{ formError }}
    </ErrorLabel>
    <LoginRouterLink />
    <OnboardingCTAContainer>
      <div class="tw-w-full tw-flex tw-flex-col tw-gap-2">
        <b-button
          pill
          block
          class="slight-rounded"
          :class="{'tw-max-w-[300px] tw-mx-auto tw-text-base md:tw-text-lg': isOnboardingRedesign}"
          variant="info"
          :disabled=" buttonDisabled"
          @click="register"
        >
          Agree and continue
        </b-button>
      </div>
    </OnboardingCTAContainer>
  </div>
</template>

<script>

// components
import formValidation from '@/components/form/mixins';
import OnboardingCTAContainer from '@/components/onboarding/OnboardingCTAContainer.vue';
import ErrorLabel from '@/components/partials/ErrorLabel.vue';

// services
import { AuthService } from '@/services/auth.service';
import { OnboardingService } from '@/services/onboarding.service';

// utils
import { isValidEmail } from '@/utils/string';
import $cookies from '@/utils/cookies';

import analyticsMixin from '@/mixins/analytics';
import { fsaHsaPromoMixin } from '@/mixins/fsaHsaPromo';

import { mapActions, mapState } from 'vuex';

import crypto from 'crypto'
import LoginRouterLink from '@/components/partials/LoginRouterLink.vue';

export default {
  components: {
    ErrorLabel,
    LoginRouterLink,
    OnboardingCTAContainer,
  },
  mixins: [formValidation, analyticsMixin, fsaHsaPromoMixin],
  props: {
    eligibilityParams: {
      type: Object,
      default() {
        return {};
      },
    },
    onboardingVariant: {
      type: String,
      default: 'NULL',
    },
    dosespotversion: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      email: '',
      password: '',
      cpassword: '',
      buttonDisabled: false,

      isPasswordShown: false,
      isCPasswordShown: false,
      initValidation: true,
      error: '',
    };
  },
  watch: {
    password: function () {
      if (!this.error.includes('@')){
        this.error = ''
      }
    }
  },
  computed: {
    ...mapState('global', ['isOnboardingRedesign']),
    formError(){
      if (this.initValidation) return false
      if (this.email.trim().length === 0 || this.password.trim().length === 0 || this.cpassword.trim().length === 0) {
        return 'All fields must be filled in. Please complete the form and try again.'
      }

      if (!this.emailState || !isValidEmail(this.email)) {
        return 'Please input a valid email.'
      }
      if (this.password !== this.cpassword) {
        return 'The passwords you entered do not match. Please enter the same password in both fields.'
      }
      if (this.error && this.error.includes(this.email)){
        return this.error
      }
      if (!this.error.includes('@')){
        return this.error
      }

      return false
    }
  },
  methods: {
    ...mapActions('onboarding', ['checkCoupon', 'applyCoupon']),
    async fbqTrack(auth) {
      try {
        // FB Pixel Tracking
        fbq('init', '348219289912097', {
          external_id: auth.userid,
          extern_id: auth.userid,
        });
        // fbq("track", "PageView");
        const pageViewBody = { userid: auth.userid };
        OnboardingService.onboardingEvent('PageView', pageViewBody);
        fbq(
          'trackCustom',
          'AccountCreated',
          {
            external_id: auth.userid,
            extern_id: auth.userid,
            ...(await this.$store.dispatch('getFBQParams')),
            state: this.eligibilityParams.state,
          },
          {
            eventID: auth.eventid,
          }
        );
      } catch (err) {
        console.log(err);
      }
    },
    async register() {
      this.initValidation = false;
      try {
        if (this.isOnboardingRedesign && (this.formError)) {
          return
        } else {
          if (this.password != this.cpassword) {
            return this.$emit('showAlert', 'The password fields do not match.');
          }

          if (this.email.trim().length == 0 || this.password.trim().length == 0) {
            return this.$emit('showAlert', 'Please fill in all the fields.');
          }

          if (!this.emailState || !isValidEmail(this.email)) {
            return this.$emit('showAlert', 'Please input a valid email.');
          }
        }

        this.buttonDisabled = true;
        const data = {
          email: this.email,
          password: this.password,
          role: 'PATIENT',
          state: this.eligibilityParams.state,
        };

        if (this.onboardingVariant) {
          data.flow_variant = this.onboardingVariant;
        }

        if (this.dosespotversion) {
          data.dosespotversion = this.dosespotversion;
        } else {
          const storedDoseSpot = localStorage.getItem('dosespotversion');
          if (storedDoseSpot) {
            data.dosespotversion = storedDoseSpot;
          }
        }
        // * Onboarding Flow Split
        const flow = $cookies.get('onboardingflow') || $cookies.get('flow');
        if (flow) {
          switch (flow) {
            case 'b':
            case 'flowB':
              data.onboardingflow = 'flowB';
              break;
            case 'a':
            case 'flowA':
              data.onboardingflow = 'flowA';
              break;
            default:
              data.onboardingflow =
                $cookies.get('onboardingflow') || $cookies.get('flow');
              break;
          }
          $cookies.remove('onboardingflow');
          $cookies.remove('flow');
        }

        // * Referrals
        if ($cookies.get('referralToken')) {
          //data.referralcode = $cookies.get("referralToken");
          this.$set(data, 'referralcode', $cookies.get('referralToken'));
        }

        const { data: auth } = await AuthService.signup(data);
        if (auth.idToken) {
          AuthService.setBearer(auth.idToken);
          const sasCookie = $cookies.get('sas_m_awin') || undefined;

          if (sasCookie != undefined && sasCookie.clickId != undefined) {
            await OnboardingService.sendSASClickId(sasCookie.clickId);
            $cookies.remove('sas_m_awin');
          }
        }

        // * FB Track
        if (auth.eventid) {
          const userid = auth.userid;
          const signature = crypto
            .createHmac('sha256', process.env.VUE_APP_INTERCOM_SECRETKEY)
            .update(userid)
            .digest('hex');
          if (userid != undefined) {
            window.analytics.identify(
              userid,
              {
                email: this.email,
                customerId: userid,
              },
              {
                integrations: {
                  Intercom: {
                    user_hash: signature,
                  },
                },
              }
            );
          }

          // * Segment Event
          await this.$_analytics_track(
            'Account Created',
            {
              customerId: auth.userid,
              messageID: auth.eventid,
              email: this.email,
              state: this.eligibilityParams.state,
            },
          );

          localStorage.setItem('authenticatedUserId', auth.userid);
          const userIdPopulated = new Event('userIdPopulated');
          window.dispatchEvent(userIdPopulated);

          // Tatari
          if (tatari) {
            console.log('Tatari Identify: ', auth.userid);
            tatari.identify(`$${[auth.userid]}`);
            console.log('Tatari Track: Account Created');
            tatari.track('Account Created');
          }

          await this.fbqTrack(auth);

          // Reboot intercom to show "Schedule a meeting" component
          window.Intercom?.('shutdown')
          window.Intercom?.('boot', {
            app_id: process.env.VUE_APP_INTERCOM_APP_ID,
            user_id: auth.userid
          });
        } else if (auth.eventid && process.env.VUE_APP_SERVER != 'production') {
          // alert('FB TRACKING AccountCreated: ' + auth.eventid);
          console.log('FB TRACKING AccountCreated');
          console.log('FB TRACKING AccountCreated: ' + auth.eventid);
        }

        // * FSA HSA sessionPromo
        if (this.$_fsaHsaPromo_isActiveFsaHsaPromo) {
          const couponResult = await OnboardingService.checkCoupon(
            this.$_fsaHsaPromo_sessionPromoValue
          );

          if (couponResult?.data?.id){
            await OnboardingService.applyCoupon({
              couponid: this.$_fsaHsaPromo_sessionPromoValue,
            });

            $cookies.remove('promocode')
          }
        }
        // * Promocode
        else if ($cookies.get('promocode') && !$cookies.get('promocodeExistingID')) {
          let getCoupon;
          try {
            getCoupon = await OnboardingService.checkCoupon(
              $cookies.get('promocode')
            );
          } catch (err) {
            getCoupon = null;
            $cookies.remove('promocode');
          }

          if (getCoupon?.data?.id) {
            await OnboardingService.applyCoupon({
              couponid: $cookies.get('promocode'),
            });

            if (getCoupon.data.hasOwnProperty('applies_to')) {
              // Skips cookie removal if code applies to Blossom
              if (
                getCoupon.data.applies_to.products[0] != 'prod_Jxqgr2Hh9YGwjZ' &&
                getCoupon.data.applies_to.products[0] != 'prod_K0rnRpVxfgGP4y'
              ) {
                this.$cookies.remove('promocode');
              }
            } else {
              this.$cookies.remove('promocode');
            }
          }
        }

        const onboardingStartedTimestamp = $cookies.get(
          'onboardingStartedTimestamp'
        );
        const identifyBody = {
          id: auth.userid,
          name: 'Onboarding Started',
          value: onboardingStartedTimestamp,
        };

        await OnboardingService.onboardingIdentify(identifyBody);

        this.buttonDisabled = false;
        this.$emit('setId', auth.userid);
        this.$emit('setEmail', this.email);
        this.$emit('setStep', 'birthday');
      } catch (err) {
        if (err.message == 'An account with the given email already exists.') {
          if (this.isOnboardingRedesign){
            this.error = `An account with the email ${this.email} already exists. You may sign in or enter a different email address to create a new account.`
          } else {
            this.$emit(
              'showEmailAlert',
              `An account with the email ${this.email} already exists. You may sign in or enter a different email address to create a new account.`,
              this.email
            );
          }
        } else {
          if (err.status == 500) {
            this.$store.dispatch('sendErrorLog', {
              step: 'Eligibility',
              error_log: {
                message: err,
              },
            });
          }
          if (this.isOnboardingRedesign){
            this.error = err.message
          } else {
            this.$emit('showAlert', err.message);
          }
        }
        this.buttonDisabled = false;
      }
    },
    limitCharacters(value) {
      return value.slice(0, 50);
    }
  },
};
</script>

<style lang="scss" scoped>
.hide-arrows::-webkit-outer-spin-button,
.hide-arrows::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}

.hide-arrows {
  -moz-appearance: textfield;
}
</style>
